import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { AppCard, AppCardContent, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import "./whatsapp-dashboard.scss";

/**
 * /whatsapp/connect/callback
 *
 * Meta redirects here after the Embedded Signup OAuth flow.
 * Reads the `code` query param and sends it to the backend to exchange
 * for an access token + auto-discover WABA + phone.
 */
const WhatsAppConnectCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const errorReason = searchParams.get("error_reason");

    if (error || errorReason) {
      setState("error");
      setMessage(searchParams.get("error_description") || errorReason || "Authorization was denied.");
      return;
    }

    if (!code) {
      setState("error");
      setMessage("No authorization code received from Meta.");
      return;
    }

    // state=platform means this is the SA/tenant-admin connect flow
    const isPlatform = searchParams.get("state") === "platform";

    (async () => {
      try {
        // Fetch canonical redirect URI from backend to match what was used in the OAuth URL
        const configEndpoint = isPlatform ? "/sa/whatsapp/connect/config" : "/whatsapp/connect/config";
        const callbackEndpoint = isPlatform ? "/sa/whatsapp/connect/callback" : "/whatsapp/connect/callback";
        let redirectUri;
        try {
          const configRes = await apiClient.get(configEndpoint);
          redirectUri = configRes.data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;
        } catch {
          redirectUri = `${window.location.origin}/whatsapp/connect/callback`;
        }
        const { data } = await apiClient.post(callbackEndpoint, { code, redirectUri });
        setState("success");
        setMessage(
          `Connected! Phone: ${data.displayPhoneNumber || data.phoneNumberId}`
        );
      } catch (err) {
        setState("error");
        setMessage(
          err?.response?.data?.message || "Failed to connect WhatsApp. Please try again."
        );
      }
    })();
  }, [searchParams]);

  return (
    <div className="wa-callback-page">
      <AppCard>
        <AppCardContent>
          <div className="wa-connect-empty">
            {state === "loading" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--loading">
                  <Loader2 size={40} className="wa-spin" />
                </div>
                <h3>Connecting WhatsApp…</h3>
                <p className="wa-text-muted">
                  Exchanging credentials with Meta. This only takes a moment.
                </p>
              </>
            )}

            {state === "success" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--success">
                  <CheckCircle size={40} />
                </div>
                <h3>WhatsApp Connected!</h3>
                <p className="wa-text-muted">{message}</p>
                <AppButton onClick={() => navigate(searchParams.get("state") === "platform" ? "/sa/whatsapp" : "/whatsapp")}>
                  <MessageCircle size={16} /> {searchParams.get("state") === "platform" ? "Go to WhatsApp Config" : "Go to WhatsApp Dashboard"}
                </AppButton>
              </>
            )}

            {state === "error" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--error">
                  <XCircle size={40} />
                </div>
                <h3>Connection Failed</h3>
                <p className="wa-text-muted">{message}</p>
                <div className="wa-inline-actions">
                  <AppButton onClick={() => navigate(searchParams.get("state") === "platform" ? "/sa/whatsapp" : "/whatsapp")}>
                    {searchParams.get("state") === "platform" ? "Back to Config" : "Back to Dashboard"}
                  </AppButton>
                  <AppButton variant="outline" onClick={() => window.close()}>
                    Close Window
                  </AppButton>
                </div>
              </>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default WhatsAppConnectCallback;
